import {
	AppButton,
	BrandedModal,
	modalCenteredMessageLinkClass
} from '@clickguard/design-system'
import { BlackFridayOfferQuoteDTO } from 'api-models'
import XClose from '@untitled-ui/icons-react/build/esm/XClose'
import moment from 'moment'
import { useEffect, useId, useRef } from 'react'
import { createPortal } from 'react-dom'
import { BlackFridayBackdrop } from './black-friday-banner'

export const formatBlackFridayDate = (timestamp: string) =>
	moment.utc(timestamp).format('MMMM D, YYYY')

export const ClaimOfferModal = ({
	open,
	onClose,
	quote,
	detailsUrl,
	onClaim,
	onRefresh,
	onRetry,
	canRetry,
	claimBlockedReason,
	canClaim,
	available,
	pending,
	submitting,
	refreshing,
	error
}: {
	open: boolean
	onClose: () => void
	quote: BlackFridayOfferQuoteDTO | null
	detailsUrl: string
	onClaim: () => void
	onRefresh: () => void
	onRetry: () => void
	canRetry: boolean
	claimBlockedReason: string | null
	canClaim: boolean
	available: boolean
	pending: boolean
	submitting: boolean
	refreshing: boolean
	error: string | null
}) => {
	const card = useRef<HTMLDivElement>(null)
	const titleId = useId()
	useEffect(() => {
		if (!open) return
		card.current
			?.closest('[role="dialog"]')
			?.setAttribute('aria-labelledby', titleId)
		const previous =
			document.activeElement instanceof HTMLElement
				? document.activeElement
				: null
		const focusable = () =>
			Array.from(
				card.current?.querySelectorAll<HTMLElement>(
					'button:not(:disabled), a[href], [tabindex="0"]'
				) || []
			).filter((item) => item.getClientRects().length > 0)
		const focusFirst = () => (focusable()[0] || card.current)?.focus()
		focusFirst()
		const trapFocus = (event: KeyboardEvent) => {
			if (event.key !== 'Tab') return
			const items = focusable()
			const first = items[0]
			const last = items[items.length - 1]
			if (!items.length || !card.current?.contains(document.activeElement)) {
				event.preventDefault()
				focusFirst()
			} else if (event.shiftKey && document.activeElement === first) {
				event.preventDefault()
				last.focus()
			} else if (!event.shiftKey && document.activeElement === last) {
				event.preventDefault()
				first.focus()
			}
		}
		document.addEventListener('keydown', trapFocus)
		return () => {
			document.removeEventListener('keydown', trapFocus)
			if (previous?.isConnected) previous.focus()
		}
	}, [open, titleId])
	if (!open) return null
	return createPortal(
		<BrandedModal
			ref={card}
			tabIndex={-1}
			open={open}
			onClose={onClose}
			width={580}
			style={{ width: 580, maxWidth: 'calc(100vw - 32px)' }}
			header={
				<div
					data-theme="light"
					className="relative flex h-24 shrink-0 items-center justify-center overflow-hidden text-white"
				>
					<BlackFridayBackdrop />
					<button
						type="button"
						aria-label="Close offer"
						onClick={onClose}
						className="absolute right-2 top-2 rounded-md p-1 text-white hover:bg-white/10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-white"
					>
						<XClose className="h-4 w-4" aria-hidden="true" />
					</button>
					<span className="relative whitespace-nowrap font-[ui-monospace,'SF_Mono',Menlo,monospace] text-[18px] sm:text-[24px] font-medium uppercase leading-[32px] tracking-[0.12em]">
						Black Friday · Pre-sale
					</span>
				</div>
			}
			title={
				<span id={titleId} className="text-[18px] leading-[24px]">
					Scale your Q4 campaigns without scaling your protection costs
				</span>
			}
			footer={
				<div className="flex w-full flex-col gap-2">
					{!canClaim && !claimBlockedReason && (
						<p className="text-[12px] leading-[16px] text-[rgb(var(--cg-text-secondary))]">
							Only workspace owners and members with billing write access can
							claim this offer.
						</p>
					)}
					{claimBlockedReason && (
						<p
							role="status"
							className="text-[12px] leading-[16px] text-[rgb(var(--cg-text-secondary))]"
						>
							{claimBlockedReason}
						</p>
					)}
					{pending && (
						<p
							role="status"
							className="text-[12px] leading-[16px] text-[rgb(var(--cg-text-secondary))]"
						>
							Your claim is being checked. You can close this window and return
							to check its status.
						</p>
					)}
					{!available && !pending && (
						<p
							role="status"
							className="text-[12px] leading-[16px] text-[rgb(var(--cg-text-secondary))]"
						>
							This offer is no longer available for this workspace.
						</p>
					)}
					{error && (
						<p
							role="alert"
							className="text-[12px] leading-[16px] text-[rgb(var(--cg-danger))]"
						>
							{error}
						</p>
					)}
					<AppButton
						variant="primary"
						size="md"
						fullWidth
						label={
							submitting
								? 'Checking your offer...'
								: pending
								? 'Claim processing...'
								: 'Claim your offer'
						}
						onClick={onClaim}
						isLoading={submitting}
						disabled={
							!canClaim || !available || !quote || pending || refreshing
						}
					/>
					{(pending || error || !available) && (
						<AppButton
							variant="secondary"
							size="sm"
							label={pending ? 'Check claim status' : 'Refresh offer'}
							onClick={onRefresh}
							isLoading={refreshing}
							disabled={submitting}
						/>
					)}
					{pending && canRetry && canClaim && available && (
						<AppButton
							variant="secondary"
							size="sm"
							label="Retry claim"
							onClick={onRetry}
							disabled={submitting || refreshing}
						/>
					)}
					{quote && (
						<p className="text-center text-[12px] leading-[16px] text-[rgb(var(--cg-text-tertiary))]">
							Current plan updates on {formatBlackFridayDate(quote.switch_at)}
						</p>
					)}
					<p className="-mb-2 mt-2 text-center text-[12px] leading-[16px] text-[rgb(var(--cg-text-tertiary))]">
						By claiming this offer, you agree to our{' '}
						<a
							href={detailsUrl}
							target="_blank"
							rel="noopener noreferrer"
							className={modalCenteredMessageLinkClass}
						>
							Terms and Conditions
						</a>
					</p>
				</div>
			}
		>
			<ul className="flex list-disc flex-col gap-1.5 pl-5 text-[14px] leading-[18px] marker:text-[rgb(var(--cg-brand))]">
				<li>Save 35% on your first year when you switch to an annual plan</li>
				{quote && (
					<li>
						Get 3x your monthly ad spend protection limit through{' '}
						{formatBlackFridayDate(quote.boost_ends_at)}
					</li>
				)}
			</ul>
		</BrandedModal>,
		document.body
	)
}
